
    import {
      ViewerApp,
      addBasePlugins,
      GroundPlugin,
      SSRPlugin,
      RendererUiPlugin,
      PopmotionPlugin,
      SimpleBackgroundEnvUiPlugin,
      TonemapPlugin,
      AssetManagerPlugin,
      InteractionPromptPlugin,
      Color,
      EasingFunctions,
    } from "https://dist.pixotronics.com/webgi/runtime/bundle-0.17.0.mjs";

    import { initWatchVariantsSwiper } from "./src/swiper.js";
    import { locale, localeAssets } from "./src/locale.js";

    import {
      initTracking,
      setTrackingDebug,
      track3dInteraction,
      trackColorSelection,
      trackLearnMore,
      trackViewIn3d,
      trackViewInAr,
    } from "./src/tracking.js";

    const INSERTION_ALIASES = {
      en: "us",
    };
    const INSERTION =
      INSERTION_ALIASES[(window.__INSERTION__ || "us").toLowerCase()] ||
      (window.__INSERTION__ || "us").toLowerCase();
    const PIXEL_DEBUG =
      (new URLSearchParams(window.location.search).get("pixelDebug") || "") ===
      "1";

    const INSERTION_VARIANT = {
      us: "US",
      uk: "UK",
      jp: "Japan",
      ch: "China",
      kr: "Korea",
      fr: "France",
      pca: "Paid Custom Audience",
    };

    async function loadConfig(ins) {
      try {
        const res = await fetch(`/insertions/${ins}.json`);
        if (!res.ok) throw new Error(`${res.status}`);
        return res.json();
      } catch {
        console.warn(`[config] Missing ${ins}.json, using empty config`);
        return { insertion: ins, pixels: {} };
      }
    }

    var viewer = null;
    var loader = null;
    var showLoader = null;
    var hideLoader = null;
    var currentModel = "silver";
    var assetManager = null;

    var deepAR = null;
    var isARMode = false;
    var currentARModel = "silver";

    const watchVariants = {
      0: {
        name: "blue",
        model: "/assets/models/Watch_1_Blue_Compress_Textures_v2.glb",
        deepar: "/assets/models/deepar/HUBLOT_Blue_export6-1.deepar",
        background: "/assets/images/blue_bg1.jpg",
      },
      1: {
        name: "silver",
        model: "/assets/models/Watch_1_Titanium_Compress_Textures_v6.glb",
        deepar: "/assets/models/deepar/HUBLOT_export7.deepar",
        background: "/assets/images/gray_bg1.jpg",
      },
      2: {
        name: "black",
        model: "/assets/models/Watch_1_Black_Compress_Textures_v2.glb",
        deepar: "/assets/models/deepar/HUBLOT_Black_export6.deepar",
        background: "/assets/images/black_b1.jpg",
      },
    };

    var hint = null;
    var hintTimer = null;
    var userTappedOnce = false;
    const HINT_DELAY = 3000;

    function setButtonImage(imgEl, src) {
      if (!imgEl || !src) return;
      if (imgEl.dataset.assetSrc === src) return;

      imgEl.style.opacity = "0";
      imgEl.addEventListener(
        "load",
        () => {
          imgEl.style.opacity = "1";
        },
        { once: true },
      );

      imgEl.dataset.assetSrc = src;
      imgEl.src = src;
    }

    document.addEventListener("DOMContentLoaded", async (event) => {
      const cfg = await loadConfig(INSERTION);
      console.log("CONFIG LOADED:", cfg);
      console.log("[locale] detected:", locale);
      setTrackingDebug(PIXEL_DEBUG);
      initTracking(cfg);

      // Apply locale-specific button images without showing fallback assets first.
      const tryArImg = document.querySelector("#try-ar-btn img");
      const viewIn3dImg = document.querySelector("#learn-more-btn img");
      setButtonImage(tryArImg, localeAssets.tryArDark);
      setButtonImage(viewIn3dImg, localeAssets.learnMoreDark);

      loader = document.querySelector(".dot-pulse-container");
      showLoader = () => (loader.style.display = "flex");
      hideLoader = () => (loader.style.display = "none");

      hint = document.getElementById("hint-cursor");
      animateRotationHint();

      showLoader();

      const discoverBtn = document.querySelector(".discover-btn");
      if (discoverBtn) {
        discoverBtn.addEventListener("mouseenter", () => {
          discoverBtn.style.transform = "scale(1.05)";
        });
        discoverBtn.addEventListener("mouseleave", () => {
          discoverBtn.style.transform = "scale(1)";
        });
      }

      const tryArBtn = document.getElementById("try-ar-btn");
      if (tryArBtn) {
        tryArBtn.style.pointerEvents = "auto";
        tryArBtn.style.cursor = "pointer";
        tryArBtn.addEventListener("click", () => {
          if (isARMode) {
            trackViewIn3d();
          } else {
            trackViewInAr();
          }
          switchMode(!isARMode);
        });
      }

      const learnMoreBtn = document.getElementById("learn-more-btn");
      if (learnMoreBtn) {
        learnMoreBtn.addEventListener("click", () => {
          trackLearnMore();
        });
      }

      initSwiper();
      setupViewer();

      setTimeout(() => {
        const swiperEl = document.getElementById("swiper-container");
        if (swiperEl) swiperEl.style.opacity = "1";
      }, 500);
    });

    async function loadDeepARScript() {
      if (window.deepar) return;

      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        let attemptCount = 0;

        const tryLoad = (scriptSrc, description) => {
          attemptCount++;

          script.src = scriptSrc;
          script.onload = () => {
            resolve();
          };

          script.onerror = () => {
            if (scriptSrc.includes("./deepar-resources/")) {
              tryLoad(
                "https://cdn.jsdelivr.net/npm/deepar@5.6.3/dist/deepar.js",
                "CDN fallback",
              );
            } else {
              reject(
                new Error("DeepAR script failed to load from all sources"),
              );
            }
          };
        };

        tryLoad("./deepar-resources/js/deepar.js", "Local resources");

        document.head.appendChild(script);
      });
    }

    async function initializeDeepAR() {
      if (deepAR) return deepAR;

      const startTime = performance.now();

      await loadDeepARScript();

      const previewElement = document.getElementById("deepar-container");

      if (!previewElement) {
        return;
      }

      previewElement.style.display = "block";

      const currentVariant = Object.values(watchVariants).find(
        (v) => v.name === currentModel,
      );
      const currentEffect = currentVariant
        ? currentVariant.deepar
        : watchVariants[1].deepar;

      try {
        const DEEPAR_KEY =
          "5908311092b0e47bc9deddef624ab38f8650d2c1784b643a36b21f0268624ec53d5eb048d98ac7a5";

        const testEffect = null;

        const isMobile =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent,
          );

        let config = {
          licenseKey: DEEPAR_KEY,
          previewElement: previewElement,
          effect: testEffect,
          rootPath: "./deepar-resources/",
          additionalOptions: {
            cameraConfig: {
              facingMode: "environment",
            },
            canvasWidth: 353,
            canvasHeight: 622,
          },
        };

        deepAR = await window.deepar.initialize(config);

        setTimeout(async () => {
          if (currentEffect && currentEffect !== testEffect) {
            try {
              await deepAR.switchEffect(currentEffect);
              const effectVariant = Object.values(watchVariants).find(
                (v) => v.deepar === currentEffect,
              );
              if (effectVariant) {
                currentARModel = effectVariant.name;
              }
            } catch (error) {
              console.error("Failed to load IWC effect:", error);
            }
          }
        }, 1000);

        deepAR.callbacks.onWristTracked = (wrist) => {
          if (hideLoader) {
            hideLoader();
          }
        };

        deepAR.callbacks.onEffectLoaded = () => {};

        deepAR.callbacks.onFaceTracked = (face) => {};

        deepAR.callbacks.onEffectLoaded = () => {};

        deepAR.callbacks.onFaceVisiblityChanged = (visible) => {};

        currentARModel = currentModel;

        return deepAR;
      } catch (error) {
        throw error;
      }
    }

    async function switchMode(toARMode) {
      const canvas3D = document.getElementById("main-canvas");
      const containerAR = document.getElementById("deepar-container");
      const backgroundImg = document.getElementById("background");
      const hintCursor = document.getElementById("hint-cursor");

      if (toARMode && !isARMode) {
        showLoader();
        try {
          await initializeDeepAR();

          if (currentModel !== currentARModel) {
            const currentVariant = Object.values(watchVariants).find(
              (v) => v.name === currentModel,
            );
            if (currentVariant && deepAR) {
              try {
                await deepAR.switchEffect(currentVariant.deepar);
                currentARModel = currentModel;
                //modelLoaded(currentARModel);
              } catch (error) {
                console.error("Failed to sync AR effect:", error);
              }
            }
          }

          canvas3D.style.display = "none";
          if (viewer) {
            viewer.enabled = false;
            viewer.scene.activeCamera.controls.autoRotate = false;
          }
          backgroundImg.style.display = "none";
          if (hintCursor) {
            hintCursor.style.display = "none";
            hintCursor.style.visibility = "hidden";
          }

          containerAR.style.display = "block";
          setTimeout(() => {
            containerAR.style.opacity = "1";
          }, 100);

          try {
            await deepAR.startCamera({
              facingMode: "environment",
              mirror: false,
            });
            //cameraActivated();
          } catch (e) {
            alert(
              "Camera access failed. Please allow camera access and try again.\nError: " +
                e.message,
            );
          }

          isARMode = true;
          const tryArBtnAR = document.getElementById("try-ar-btn");
          if (tryArBtnAR) {
            setButtonImage(
              tryArBtnAR.querySelector("img"),
              localeAssets.viewIn3d,
            );
          }
          const logoBlackAR = document.getElementById("logo-black");
          const logoWhiteAR = document.getElementById("logo-white");
          if (logoBlackAR) logoBlackAR.style.opacity = "0";
          if (logoWhiteAR) logoWhiteAR.style.opacity = "1";
          const learnMoreAR = document.getElementById("learn-more-btn");
          if (learnMoreAR) {
            setButtonImage(
              learnMoreAR.querySelector("img"),
              localeAssets.learnMoreAr,
            );
          }
        } catch (error) {
          alert(
            "Failed to initialize AR. Please check your DeepAR license key and files.",
          );
          hideLoader();
        }
      } else if (!toARMode && isARMode) {
        if (deepAR) {
          try {
            await deepAR.stopCamera();
          } catch (error) {}
        }

        containerAR.style.opacity = "0";
        setTimeout(() => {
          containerAR.style.display = "none";
        }, 500);

        canvas3D.style.display = "block";
        if (viewer) {
          viewer.enabled = true;
          viewer.renderer.refreshPipeline();
          setTimeout(() => {
            viewer.scene.activeCamera.controls.autoRotate = true;
          }, 1000);
        }
        backgroundImg.style.display = "block";

        isARMode = false;
        updateUIForVariant(currentModel);

        if (currentModel !== currentARModel) {
          for (let i = 0; i < Object.keys(watchVariants).length; i++) {
            if (watchVariants[i].name === currentARModel) {
              setTimeout(() => loadWatchModel(i), 100);
              break;
            }
          }
        }
      }
    }

    function updateUIForVariant(variantName) {
      const loaderEl = document.querySelector(".dot-pulse-container");
      const tryArBtnEl = document.getElementById("try-ar-btn");
      const learnMoreBtnEl = document.getElementById("learn-more-btn");
      const isBlack = variantName === "black";
      if (loaderEl) loaderEl.classList.remove("dot-dark");
      if (tryArBtnEl && !isARMode) {
        setButtonImage(
          tryArBtnEl.querySelector("img"),
          isBlack ? localeAssets.tryArLight : localeAssets.tryArDark,
        );
      }
      if (learnMoreBtnEl) {
        setButtonImage(
          learnMoreBtnEl.querySelector("img"),
          isBlack ? localeAssets.learnMoreLight : localeAssets.learnMoreDark,
        );
      }
    }

    // Load AR model
    async function loadARModel(variantIndex) {
      if (!deepAR || !isARMode) {
        return;
      }

      const variant = watchVariants[variantIndex];
      if (!variant) {
        return;
      }

      if (currentARModel === variant.name) {
        return;
      }

      try {
        await deepAR.switchEffect(variant.deepar);
        currentARModel = variant.name;
      } catch (error) {
        console.error("Error loading AR model:", error);
        throw error;
      }
    }

    // Smooth background transition function
    function changeBackgroundSmooth(newBackgroundSrc) {
      const blueBg = document.getElementById("background");
      const blackBg = document.getElementById("background-black");
      const grayBg = document.getElementById("background-gray");
      const logoBlack = document.getElementById("logo-black");
      const logoWhite = document.getElementById("logo-white");

      if (!blueBg || !blackBg) return;

      const isBlue = newBackgroundSrc.includes("blue_bg");
      const isBlack = newBackgroundSrc.includes("black_b1");
      const isGray = newBackgroundSrc.includes("gray_bg");

      blueBg.style.transition = "opacity 0.6s ease-in-out";
      blackBg.style.transition = "opacity 0.6s ease-in-out";
      if (grayBg) grayBg.style.transition = "opacity 0.6s ease-in-out";

      if (isBlue && !blueBg.src && blueBg.dataset.src) blueBg.src = blueBg.dataset.src;
      if (isBlack && !blackBg.src && blackBg.dataset.src) blackBg.src = blackBg.dataset.src;

      blueBg.style.opacity = isBlue ? "1" : "0";
      blackBg.style.opacity = isBlack ? "1" : "0";
      if (grayBg) grayBg.style.opacity = isGray ? "1" : "0";

      const isDark = isBlack;
      if (logoBlack) logoBlack.style.opacity = isDark ? "0" : "1";
      if (logoWhite) logoWhite.style.opacity = isDark ? "1" : "0";
    }

    async function loadWatchModel(variantIndex) {
      if (isARMode) {
        const variant = watchVariants[variantIndex];
        if (!variant || currentARModel === variant.name) return;

        trackColorSelection(variant.name);

        showLoader();

        try {
          await loadARModel(variantIndex);
          hideLoader();
        } catch (error) {
          hideLoader();
        }
      } else {
        const variant = watchVariants[variantIndex];
        if (!variant || currentModel === variant.name) return;

        trackColorSelection(variant.name);

        showLoader();

        try {
          const manager = viewer.getManager();
          const importer = manager.importer;
          const canvas = document.getElementById("main-canvas");

          canvas.style.transition = "opacity 0.3s ease-in-out";
          canvas.style.opacity = "0";

          await new Promise((resolve) => setTimeout(resolve, 300));

          if (variant.background) {
            changeBackgroundSmooth(variant.background);
          }
          updateUIForVariant(variant.name);

          const rawAssets = await importer.importPath(variant.model, {
            processImported: false,
          });

          const processed = await importer.processImported(rawAssets, {
            autoScale: true,
            pseudoCenter: true,
          });

          viewer.scene.disposeSceneModels();
          viewer.renderer.refreshPipeline();
          await manager.addProcessedAssets(processed);

          canvas.style.opacity = "1";

          setTimeout(() => {
            animateArrows();
          }, 500);

          currentModel = variant.name;
          //modelLoaded(currentModel);
          hideLoader();
        } catch (error) {
          console.error("Error loading model:", error);
          hideLoader();
        }
      }
    }

    async function setupViewer() {
      const canvas = document.getElementById("main-canvas");
      const poster = document.getElementById("poster");
      const spinnerContainer = document.querySelector(".dot-pulse-container");

      viewer = new ViewerApp({
        canvas: document.getElementById("main-canvas"),
      });

      await addBasePlugins(viewer);

      assetManager = viewer.getPlugin(AssetManagerPlugin);
      if (!assetManager) {
        assetManager = await viewer.addPlugin(AssetManagerPlugin);
      }

      await assetManager.addFromPath(
        "/assets/models/Watch_1_Titanium_Compress_Textures_v6.glb",
        {
          autoScale: true,
          pseudoCenter: true,
        },
      );
      await viewer.setEnvironmentMap("/assets/hdr/neutral_3.hdr");

      const envTexture = viewer.scene.environment;

      if (envTexture) {
        envTexture.rotation = 1.4;
        envTexture.needsUpdate = true;
      }

      const controls = viewer.scene.activeCamera.controls;

      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.rotateSpeed = 2.0;
      controls.minDistance = 7.0;
      controls.maxDistance = 12.0;
      controls.autoRotateSpeed = 1.0;
      controls.enablePan = false;
      controls.enableZoom = true;

      // Set initial camera distance 1.2x further out (position0.z was 10)
      controls.position0.z = 12.0;
      controls.position0.y = -1;
      controls.target0.y = -0.2;
      controls.reset();

      viewer.getPlugin(GroundPlugin).enabled = false;
      viewer.getPlugin(SSRPlugin).enabled = false;

      // Make background transparent - this is the key!
      const tonemapPlugin = viewer.getPlugin(TonemapPlugin);
      tonemapPlugin.config.clipBackground = true;
      tonemapPlugin.config.exposure = 1.0; // Set HDR intensity

      // Disable bloom effect
      //tonemapPlugin.viewer.renderer.passes.bloom = false;
      //   console.log(tonemapPlugin);

      const renderer = viewer.renderer.passes;
      if (renderer) {
        const bloomPass = renderer.find((pass) => pass.passId === "bloom");
        if (bloomPass) {
          bloomPass.enabled = false;
          // console.log("true");
          // bloomPass.passObject.power = 0.1;
          // bloomPass.passObject.radius = 0.2;
          // bloomPass.passObject.softThreshold = 0.2;
          // bloomPass.passObject.prefilter = {w: 0.06, x: 1.0, y: 0.3, z: 1.0};
          // console.log(bloomPass.passObject.power);
        }
      }

      // rendererUI.scale = window.devicePixelRatio;
      const ratio = window.devicePixelRatio;
      const rendererUI = await viewer.addPlugin(RendererUiPlugin);

      // Cap device pixel ratio for better performance
      if (ratio > 2.0) {
        rendererUI.scale = 2; // Reduced from 2.0 for better performance
      } else {
        rendererUI.scale = 1.5; // Standard for lower DPI devices
      }

      const interactionPrompt = viewer.getPlugin(InteractionPromptPlugin);
      interactionPrompt.enabled = false;

      const background = await viewer.addPlugin(SimpleBackgroundEnvUiPlugin);
      background.sceneBackground = new Color(0x000000);

      setTimeout(() => {
        canvas.classList.add("visible");
        poster.style.display = "none"; // Hide poster when 3D model is ready
        hideLoader(); // Hide dot loader when 3D model is ready
        startHintTimer(); // Start hint cursor timer

        viewer.renderer.refreshPipeline();
        animateArrows();
        if (spinnerContainer) {
          spinnerContainer.style.display = "none";
        }
      }, 750);

      setTimeout(() => {
        // const interval = setInterval(function() {
        // 	animateRotationHint();
        // }, 10000);
      }, 3000);

      setTimeout(() => {
        controls.autoRotate = true;
      }, 4000);

      // Add screenshot functionality
      setupScreenshotButton();

      // HDR rotation slider
      const hdrSlider = document.getElementById("hdr-rotation-slider");
      const hdrValue = document.getElementById("hdr-rotation-value");
      if (hdrSlider) {
        hdrSlider.addEventListener("input", () => {
          const val = parseFloat(hdrSlider.value);
          hdrValue.textContent = val.toFixed(2);
          const env = viewer.scene.environment;
          if (env) {
            env.rotation = val;
            env.needsUpdate = true;
            viewer.setDirty();
          }
        });
      }

      // Add performance optimization - pause animations when page not visible
      setupVisibilityOptimization();

      // Add interaction listeners to stop hint
      const handleInteractionStart = () => {
        userTappedOnce = true;

        if (hint) hint.style.display = "none";
        if (hintTimer) {
          clearInterval(hintTimer);
          hintTimer = null;
        }
      };

      let controlsGestureActive = false;
      let wheelGestureIdleTimer = null;
      const WHEEL_GESTURE_IDLE_MS = 220;

      const begin3dGesture = () => {
        if (isARMode) return;
        handleInteractionStart();
        if (controlsGestureActive) return;
        controlsGestureActive = true;
        track3dInteraction();
      };

      const end3dGesture = () => {
        controlsGestureActive = false;
      };

      canvas.addEventListener("pointerdown", begin3dGesture);
      canvas.addEventListener("pointerup", end3dGesture);
      canvas.addEventListener("pointercancel", end3dGesture);
      canvas.addEventListener("touchstart", begin3dGesture, { passive: true });
      canvas.addEventListener("touchend", end3dGesture, { passive: true });
      canvas.addEventListener("touchcancel", end3dGesture, { passive: true });
      canvas.addEventListener("wheel", () => {
        begin3dGesture();
        if (wheelGestureIdleTimer) {
          clearTimeout(wheelGestureIdleTimer);
        }
        wheelGestureIdleTimer = setTimeout(() => {
          end3dGesture();
          wheelGestureIdleTimer = null;
        }, WHEEL_GESTURE_IDLE_MS);
      }, { passive: true });

      return;
    }

    function initSwiper() {
      const swiperContainer = document.getElementById("swiper-container");
      if (swiperContainer) {
        swiperContainer.style.display = "block";
      }

      const swiper = initWatchVariantsSwiper((activeIndex) => {
        loadWatchModel(activeIndex);
      });

      return swiper;
    }

    function animateRotationHint() {
      if (userTappedOnce) return;
      if (!hint) return;

      hint.style.display = "block";
      hint.classList.add("animateRight");
      hint.addEventListener("animationend", (event) => {
        if (event.animationName === "moveRight1") {
          hint.classList.add("animateLeft");
        } else if (event.animationName === "moveLeft") {
          hint.classList.add("animateToInitial");
        } else if (event.animationName === "moveRight2") {
          hint.style.display = "none";
          hint.classList.remove(
            "animateRight",
            "animateLeft",
            "animateToInitial",
          );
        }
      });
    }

    function startHintTimer() {
      if (hintTimer) {
        clearInterval(hintTimer);
      }
      hintTimer = setInterval(() => {
        if (!userTappedOnce && !isARMode) {
          animateRotationHint();
        }
      }, HINT_DELAY + 2000);
    }

    function setupScreenshotButton() {
      document
        .getElementById("screenshot-btn")
        .addEventListener("click", () => {
          // Stop auto rotation temporarily for a clean screenshot
          const controls = viewer.scene.activeCamera.controls;
          const wasAutoRotating = controls.autoRotate;
          controls.autoRotate = false;

          setTimeout(() => {
            // Get the canvas from WebGI viewer
            const canvas = document.getElementById("main-canvas");

            // Force a render to ensure everything is up to date
            viewer.renderer.refreshPipeline();

            setTimeout(() => {
              // Capture the canvas as image
              canvas.toBlob((blob) => {
                if (blob) {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "hublot-watch-poster.png";
                  a.click();
                  URL.revokeObjectURL(url);
                }
              }, "image/png");

              // Restore auto rotation if it was enabled
              if (wasAutoRotating) {
                controls.autoRotate = true;
              }
            }, 100);
          }, 100);
        });
    }

    function setupVisibilityOptimization() {
      document.addEventListener("visibilitychange", () => {
        if (!viewer) return;

        const controls = viewer.scene.activeCamera.controls;

        if (document.hidden) {
          controls.autoRotate = false;
          if (
            viewer.renderer &&
            typeof viewer.renderer.setFrameRate === "function"
          ) {
            viewer.renderer.setFrameRate(10);
          }
        } else {
          if (!isARMode) {
            setTimeout(() => {
              controls.autoRotate = true;
            }, 1000);
          }
          if (
            viewer.renderer &&
            typeof viewer.renderer.setFrameRate === "function"
          ) {
            viewer.renderer.setFrameRate(60);
          }
        }
      });

      window.addEventListener("blur", () => {
        if (
          viewer &&
          viewer.renderer &&
          typeof viewer.renderer.setFrameRate === "function"
        ) {
          viewer.renderer.setFrameRate(15);
        }
      });

      window.addEventListener("focus", () => {
        if (
          viewer &&
          viewer.renderer &&
          typeof viewer.renderer.setFrameRate === "function"
        ) {
          viewer.renderer.setFrameRate(60);
        }
      });
    }

    async function animateArrows() {
      const popmotion = await viewer.addPlugin(PopmotionPlugin);
      const now = new Date();

      // Collect all hand mesh objects by name prefix
      const handSuffix =
        { silver: "", blue: "001", black: "003" }[currentModel] ?? "";
      const allSceneObjects = [];
      viewer.scene.traverse((obj) => {
        allSceneObjects.push(obj);
      });
      const secondsRoot = allSceneObjects.find(
        (obj) => obj.name === `Hand_chronograph${handSuffix}`,
      );
      const minutesRoot = allSceneObjects.find(
        (obj) => obj.name === `Hand_minutes${handSuffix}`,
      );
      const hoursRoot = allSceneObjects.find(
        (obj) => obj.name === `Hand_hours${handSuffix}`,
      );

      if (secondsRoot) {
        // Get the built-in offset of the seconds hand in the 3D model
        const modelOffset = secondsRoot.rotation.z;

        // Calculate target position based on current time, accounting for model offset
        const seconds = now.getSeconds();
        const targetAngle = (-Math.PI * 2 * seconds) / 60 + modelOffset;

        // Start from 12:00 position (adding model offset to compensate)
        const startAngle = modelOffset;

        // Always start animation from 12:00 position to current time
        popmotion.animate({
          from: startAngle,
          to: targetAngle,
          duration: 2000,
          ease: EasingFunctions.easeInOut,
          onUpdate: (v) => {
            secondsRoot.rotation.set(0, 0, v);
            viewer.setDirty();
          },
          onComplete: () => {
            // Continue with real-time animation
            popmotion.animate({
              from: targetAngle,
              to: targetAngle - 2 * Math.PI,
              duration: 60 * 1000,
              repeat: 10000,
              ease: EasingFunctions.linear,
              onUpdate: (v) => {
                secondsRoot.rotation.set(0, 0, v);
                viewer.setDirty();
              },
            });
          },
        });
      }

      if (minutesRoot) {
        // minutes
        const minutes = now.getMinutes() + now.getSeconds() / 60;
        const minutesAngle = (Math.PI * 2 * minutes) / 60;
        const minutesInitAngle = (Math.PI * 2 * 7) / 60; // start at :07
        const minutesToAngle =
          -minutesAngle <= -minutesInitAngle
            ? -minutesAngle
            : -minutesAngle - 2 * Math.PI;

        popmotion.animate({
          from: -minutesInitAngle,
          to: minutesToAngle,
          duration: 2000,
          ease: EasingFunctions.easeInOut,
          onUpdate: (v) => {
            minutesRoot.rotation.set(0, 0, v);
            viewer.setDirty();
          },
          onComplete: () => {
            popmotion.animate({
              from: minutesToAngle,
              to: minutesToAngle - 2 * Math.PI,
              duration: 60 * 60 * 1000,
              repeat: 10000,
              ease: EasingFunctions.linear,
              onUpdate: (v) => {
                minutesRoot.rotation.set(0, 0, v);
                viewer.setDirty();
              },
            });
          },
        });
      }

      if (hoursRoot) {
        // hours
        const hours = (now.getHours() % 12) + now.getMinutes() / 60;
        const hoursAngle = (Math.PI * 2 * hours) / 12;
        const hoursInitAngle = (Math.PI * 2 * (10 + 7 / 60)) / 12; // start at 10:07
        const hoursTOAngle =
          -hoursAngle <= -hoursInitAngle
            ? -hoursAngle
            : -hoursAngle - 2 * Math.PI;

        popmotion.animate({
          from: -hoursInitAngle,
          to: hoursTOAngle,
          duration: 2000,
          ease: EasingFunctions.easeInOut,
          onUpdate: (v) => {
            hoursRoot.rotation.set(0, 0, v);
            viewer.setDirty();
          },
          onComplete: () => {
            popmotion.animate({
              from: hoursTOAngle,
              to: hoursTOAngle - 2 * Math.PI,
              duration: 60 * 60 * 12 * 1000,
              repeat: 10000,
              ease: EasingFunctions.linear,
              onUpdate: (v) => {
                hoursRoot.rotation.set(0, 0, v);
                viewer.setDirty();
              },
            });
          },
        });
      }
    }
  